










































































import Vue, { PropType } from 'vue'
import { randomKey, isValidUrl, unmask } from '@/utility/helpers'
import { debounce } from 'debounce'
import moment from 'moment'
import Button from '@/types/components/Button.interface'
import DatePicker from '@/components/DatePicker.vue'
import TriPanelTools from './TriPanelTools.vue'
export default Vue.extend({
    components: { DatePicker, TriPanelTools },
    props: {
        //  The buttons shown after the 'Label'
        buttons: {
            type: Array as PropType<Button[]>,
            default: null
        },
        //  The handler for the change event
        callback: {
            type: Function,
            default: null
        },
        //  Replace the text input with a datetime picker
        date: {
            type: Boolean,
            default: false
        },
        //  Indicates if the component is editable
        editable: {
            type: Boolean,
            default: false
        },
        //  The name of the filter applied to the value
        filter: {
            type: String,
            default: null
        },
        //  The Vuetify enum for font size
        fontClass: {
            type: String,
            default: 'caption'
        },
        //  The icon displayed to the left of the value
        icon: {
            type: String,
            default: null
        },
        iconClass: {
            type: [String, Object],
            default: null
        },
        //  The color of the icon displayed to the left of the value
        iconColor: {
            type: String,
            default: null
        },
        //  The text displayed above the 'value'
        label: {
            type: String,
            default: null
        },
        //  the icon displayed to the left of the 'label'
        labelIcon: {
            type: String,
            default: null
        },
        //  The mask applied to our value
        mask: {
            type: String,
            default: null
        },
        //  The name of the input (required for validation purposes)
        name: {
            type: String,
            required: true
        },
        //  Applies the 'outline' style
        outlined: {
            type: Boolean,
            default: false
        },
        //  The text displayed when a value does not exist
        placeholder: {
            type: String,
            default: null
        },
        //  Replaces the 'text-field' with a select element
        select: {
            type: Boolean,
            default: false
        },
        //  The options available to the 'select' element
        selectData: {
            type: Array,
            default: null
        },
        scope: {
            type: String,
            default: null
        },
        //  Replaces the 'text-field' with a 'textarea'
        textarea: {
            type: Boolean,
            default: false
        },
        //  VeeValidate rules applied to the input
        validate: {
            type: String,
            default: null
        },
        //  The value displayed in the component
        value: {
            type: [String, Number],
            default: null
        }
    },
    data: () => ({
        //  A random uuid (used to select the display element)
        key: randomKey(),
        //  The v-model of the input (we use this for better performance, and validation)
        temp: null
    }),
    mounted() {
        //  Set the v-model bound to the input
        this.setTemp(this.value)
    },
    computed: {
        //  The class applied to the input text
        inputClass() {
            //  If the value is null, we use italic font for the 'no value' placeholder
            const italic = !this.temp ? 'italic-input' : ''

            //  If the component is in editing mode, we shown purple text, or show red if errors exist
            const color =
                this.errors.collect(this.name).length > 0
                    ? 'red-text'
                    : this.editable
                    ? 'purple-text'
                    : 'grey-text'

            //  Assemble the class string
            const cls = `${this.fontClass} ${color} ${italic}`

            return cls
        },
        labelStyle() {
            return {
                marginLeft: this.labelIcon ? '5px' : ''
            }
        }
    },
    methods: {
        //  Focus on the input
        focusNow(): void {
            //  Get the element
            const el = document.getElementById(this.key)

            //  Focus on the element (we use $nextTick to make the focus work)
            this.$nextTick(() => el.focus())
        },
        //  Determine if a string is a valid url
        isValidUrl(val: string): boolean {
            //  Check if the string is a valid url
            const valid = isValidUrl(val)

            //  Return the result
            return valid
        },
        //  Navigate to an external website
        goToLink(link: string) {
            //  Check if we're runnin in a native app (we want to open a new window, rather than a new tab is it is mobile)
            const isApp = this.$store.getters.isNativeApp

            //  Determine if we pop a new tab, or new window
            const option = isApp ? '_system' : '_blank'

            //  Navigate to the link
            window.open(link, option)
        },
        onBlur() {
            //  Tell our parent
            this.callback(this.temp)
        },
        //  Handle a button click
        onButtonClick(button: Button) {
            //  Run the button's callback function
            button.callback(this)
        },
        //  Handle the 'change' event on the select input
        onChange(val): void {
            //  Get the input element
            const el = this.$refs[this.key]

            //  Unfocus the element
            el.blur()
        },
        //  Handle the 'input' event from the date picker
        onDateInput(val: string): void {
            //  Convert value to date
            const formatted = val ? moment(val).utc().format('MM/DD/YYYY') : null

            //  Set our value
            if (this.callback) this.callback(formatted)
        },
        //  Handle the text field input event
        onInput: debounce(async function (val): Promise<void> {
            //  Validate the input
            const valid = await this.$validator.validate()

            //  If the value is NOT valid, do nothing
            // if (!valid) return

            // We only apply our masking and filters if the number entered is a valid number
            if (valid) {
                //  If a mask is set, unmask the value
                if (this.mask) val = unmask(val, this.mask)

                //  If the filter is 'phone' we want to format the value as a phone number 
                if (this.filter === 'phone') val = this.$options.filters.phone(val)
            }

            //  Else, if a 'submit' handler exists, use it!
            if (this.callback) this.callback(val)
        }, 200),
        //  Handle the 'enter' keypress event (we want to unfocus the element)
        onKeyUpEnter(event) {
            //  Unfocus the the element aside from notes
            if (this.name !== 'Notes') event.target.blur()
        },
        //  Set the v-model the input is bound to (we need some special validator handling here)
        setTemp(val: string | number): void {
            /*
             * Vee-validate throws a console error if we don't do some hokey pokey here.
             * Read more here: https://github.com/logaretm/vee-validate/issues/1828
             */

            //  Pause the $validtador
            this.$validator.pause()

            //  If a filter exists, apply it now
            if (this.filter) val = this.$options.filters[this.filter](val)

            //  Update the temp value
            this.temp = val

            //  Resume the validator
            this.$nextTick(() => {
                this.$validator.reset()
                this.$validator.resume()
            })
        }
    },
    watch: {
        editable(val: boolean): void {
            //  If we are setting this to true, do nothing
            if (val) return

            //  If we stop editing, then set this to our val
            this.setTemp(this.value)
        },
        value(val): void {
            //  Keep our 'temp' v-model in sync with the value
            this.setTemp(val)
        }
    }
})
